import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, usePinch, useWheel } from '@use-gesture/react';
import DangerouslySetInnerHTML from '../../components/DangerouslySetInnerHTML';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { BaseModalProps } from '../../ui/uiSlice';
import { AssetTokenProvider } from '../AssetTokenContext';
import EmbedContent from '../embed/EmbedContent';
import IFrameComponent from '../iframe/IFrameComponent';
import type { UploadedCarouselItem } from '../image/types';
import CarouselModalContent from './CarouselModalContent';
import * as styles from './EditorModal.module.scss';

const ENTITY_ASSET_TOKEN_FRAGMENT = gql(`
  fragment EditorModal_entityAssetToken on EntityAssetToken {
    ...AssetTokenProvider_entityAssetToken
  }
`);

interface ContainerProps {
  className: string;
  children: React.ReactNode;
}

const Container = ({ className, children }: ContainerProps) => {
  return (
    <div className={classNames(styles.container, className)}>{children}</div>
  );
};

interface CodeModalProps {
  compiledHTML: string;
}

const CodeModal = ({ compiledHTML }: CodeModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    // https://use-gesture.netlify.app/docs/gestures/#about-the-pinch-gesture
    const skipAccessibilityZoom = (e: Event) => {
      e.preventDefault();
    };
    document.addEventListener('gesturestart', skipAccessibilityZoom);
    document.addEventListener('gesturechange', skipAccessibilityZoom);
    return () => {
      document.removeEventListener('gesturestart', skipAccessibilityZoom);
      document.removeEventListener('gesturechange', skipAccessibilityZoom);
    };
  }, []);

  useWheel(
    (e) => {
      if (e.ctrlKey) return; // Pinch
      setScale((current) => Math.max(current + e.delta[1] / 1000, 1.0));
    },
    { target: contentRef },
  );

  usePinch((e) => setScale((current) => Math.max(current + e.delta[0], 1.0)), {
    target: contentRef,
  });

  useDrag(
    (e) =>
      setTranslate((current) => [
        current[0] + e.delta[0] / scale,
        current[1] + e.delta[1] / scale,
      ]),
    { target: contentRef },
  );

  return (
    <Container className={styles.code}>
      <div ref={contentRef} className={styles.content}>
        <DangerouslySetInnerHTML html={compiledHTML}>
          <div
            className={styles.previewContainer}
            style={{
              transform: `scale(${String(scale)}) translate(${String(
                translate[0],
              )}px, ${String(translate[1])}px)`,
            }}
          />
        </DangerouslySetInnerHTML>
      </div>
    </Container>
  );
};

type EntityAssetTokenFragment = DocumentType<
  typeof ENTITY_ASSET_TOKEN_FRAGMENT
>;

interface EditorModalProps extends BaseModalProps {
  embed?: {
    id: string;
    readOnly: boolean;
    type: string;
  };
  iframe?: string;
  carousel?: {
    images: UploadedCarouselItem[];
    selectedIndex: number;
    entityAssetToken: EntityAssetTokenFragment;
  };
  code?: {
    compiledHTML: string;
  };
}

const EditorModal = ({ embed, iframe, code, ...props }: EditorModalProps) => {
  if (props.carousel) {
    const { entityAssetToken, ...carousel } = props.carousel;
    return (
      <AssetTokenProvider entityAssetToken={entityAssetToken}>
        <Container className={styles.carousel}>
          <CarouselModalContent
            images={carousel.images}
            selectedIndex={carousel.selectedIndex}
          />
        </Container>
      </AssetTokenProvider>
    );
  }

  if (embed) {
    return (
      <Container className={styles.embed}>
        <div className={classNames(styles.content, styles.scrollContainer)}>
          <EmbedContent
            id={embed.id}
            readOnly={embed.readOnly}
            type={embed.type}
            previewMode="fullScreen"
          />
        </div>
      </Container>
    );
  }

  if (iframe) {
    return (
      <Container className={styles.iframe}>
        <div className={classNames(styles.content, styles.scrollContainer)}>
          <IFrameComponent src={iframe} />
        </div>
      </Container>
    );
  }

  if (code) {
    return <CodeModal compiledHTML={code.compiledHTML} />;
  }

  return null;
};

export default EditorModal;
